import { useState } from "react";
import { PVE_SPELLS, SPELL_POOL, spellsUnlockedAt } from "../../stadium-trial/content/pveSpells";
import type { PveSpellId } from "../../stadium-trial/content/pveSpells";
import { ROTATION_GAUGE_SCALE } from "../../stadium-trial/engine/pveCombatInteractive";
import { FONT, ST_BEVEL, ST_STYLE } from "../../stadium-trial/components/StadiumTrialScreen";
import {
  DUEL_ROTATION_SIZE,
  MIN_DUEL_ROTATION,
  ROTATION_SLOT_LEVELS,
  duelRotationSlots,
  DEFAULT_ATTACK_ROTATION,
  DEFAULT_DEFENSE_ROTATION,
  normalizeRotation,
  estimateDuelSpellDamage,
} from "../engine/duelRotationCombat";

// ─── Duel spell setup — Ulala-style slotted rotations ─────────────────────────
// Four ordered SLOT rows (the slot index IS the rotation order). Tap a slot to
// open a spell picker and assign a spell to that exact position — so the player
// controls the firing order directly. Each spell shows its mana cost in a
// teardrop badge tucked in the corner of its icon.
//
// AUTOSAVE: every valid change (≥MIN_DUEL_ROTATION spells in each loadout) is
// saved immediately — no Save button.
//
// Slots are level-gated (ROTATION_SLOT_LEVELS): 2 at start, 3rd at Lv 10,
// 4th at Lv 20 — a 2-spell fast loop is a legitimate build, not a fallback.

const C = { text: "#E8F0FF", muted: "#8fa3c2", gold: "#F5B301", turf: "#2DE36B" };

type Slots = (PveSpellId | null)[];

// Fixed-length slot array (length = unlocked slots), padded with nulls.
function toSlots(ids: string[] | undefined, fallback: PveSpellId[], slots: number): Slots {
  const norm = normalizeRotation(ids, fallback, slots).slice(0, slots) as PveSpellId[];
  const out: Slots = Array.from({ length: slots }, (_, i) => norm[i] ?? null);
  return out;
}

// ── Teardrop mana badge ────────────────────────────────────────────────────────
// Mana cost badge — always MANA BLUE (energy color), never the spell's color:
// the drop reads as "energy cost" regardless of the spell's theme.
function ManaDrop({ cost }: { cost: number; color?: string }) {
  return (
    <span style={{
      position: "absolute", bottom: -4, right: -4, width: 20, height: 20,
      background: "#4CC9F0", borderRadius: "0 50% 50% 50%", transform: "rotate(45deg)",
      display: "grid", placeItems: "center",
      boxShadow: "0 1px 3px rgba(0,0,0,0.45)", border: "1.5px solid rgba(8,12,20,0.9)",
    }}>
      <span style={{ transform: "rotate(-45deg)", font: `700 10px ${FONT.display}`, color: "#0c1322" }}>{cost}</span>
    </span>
  );
}

export function DuelSpellSetup({ playerLevel, playerPhysique, attack, defense, onSave, onClose }: {
  playerLevel: number;
  playerPhysique: number;
  attack?: string[];
  defense?: string[];
  onSave: (attack: string[], defense: string[]) => void;
  onClose: () => void;
}) {
  const slots = duelRotationSlots(playerLevel);
  const [tab, setTab] = useState<"attack" | "defense">("attack");
  const [atkSlots, setAtkSlots] = useState<Slots>(() => toSlots(attack, DEFAULT_ATTACK_ROTATION, slots));
  const [defSlots, setDefSlots] = useState<Slots>(() => toSlots(defense, DEFAULT_DEFENSE_ROTATION, slots));
  const [picking, setPicking] = useState<number | null>(null); // slot index being edited
  const unlocked = spellsUnlockedAt(playerLevel);

  const current = tab === "attack" ? atkSlots : defSlots;
  const filled = (s: Slots) => s.filter(Boolean) as PveSpellId[];

  // Estimated duel damage of a spell for the player's Puissance (avg hit, non-crit,
  // pre-defense) — lets the player project how hard the attack hits. "" for
  // non-damage spells (Focus, Wall).
  const dmgLabel = (id: PveSpellId) => {
    const d = estimateDuelSpellDamage(playerPhysique, id);
    return d > 0 ? ` (≈${d})` : "";
  };

  // Autosave — persist as soon as BOTH loadouts are valid (≥ MIN spells).
  const applyChange = (next: Slots) => {
    const nextAtk = tab === "attack" ? next : atkSlots;
    const nextDef = tab === "defense" ? next : defSlots;
    if (tab === "attack") setAtkSlots(next); else setDefSlots(next);
    const a = filled(nextAtk), d = filled(nextDef);
    if (a.length >= MIN_DUEL_ROTATION && d.length >= MIN_DUEL_ROTATION) onSave(a, d);
  };

  // Assign a spell to a slot; if it already sits in another slot, swap it out
  // (rotations stay unique — clearer builds).
  const assign = (slot: number, id: PveSpellId | null) => {
    const next = [...current];
    if (id && next.includes(id)) next[next.indexOf(id)] = null;
    next[slot] = id;
    applyChange(next);
    setPicking(null);
  };

  const total = filled(current).length;
  const valid = total >= MIN_DUEL_ROTATION;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <style>{ST_STYLE}</style>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", background: "linear-gradient(180deg,#141d31,#0b1120)", borderTop: "1px solid rgba(255,255,255,0.1)", borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: "20px 16px calc(20px + env(safe-area-inset-bottom, 0px))", display: "flex", flexDirection: "column", gap: 12, fontFamily: FONT.ui }}>

        <div style={{ font: `700 19px ${FONT.display}`, letterSpacing: 2, textTransform: "uppercase", color: C.text, textAlign: "center" }}>Spell Rotations</div>
        <div style={{ font: `500 11px ${FONT.ui}`, color: C.muted, textAlign: "center", lineHeight: 1.4 }}>
          Tap a slot to set its spell — the slot order IS your rotation order, and it saves automatically. Your defense rotation fights for you when you get attacked.
        </div>

        {/* Attack / Defense tabs */}
        <div style={{ display: "flex", gap: 8 }}>
          {(["attack", "defense"] as const).map((k) => {
            const on = tab === k;
            const count = filled(k === "attack" ? atkSlots : defSlots).length;
            return (
              <button key={k} onClick={() => { setTab(k); setPicking(null); }} style={{
                flex: 1, padding: "10px 0", cursor: "pointer", borderRadius: 12,
                border: `1.5px solid ${on ? (k === "attack" ? "#FF6B6B" : "#4CC9F0") : "rgba(255,255,255,0.1)"}`,
                background: on ? (k === "attack" ? "rgba(255,107,107,0.12)" : "rgba(76,201,240,0.12)") : "rgba(255,255,255,0.03)",
                boxShadow: on ? `0 0 16px ${k === "attack" ? "rgba(255,107,107,0.35)" : "rgba(76,201,240,0.35)"}` : "none",
                font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase",
                color: on ? C.text : C.muted,
              }}>
                {k === "attack" ? "⚔️ Attack" : "🛡️ Defense"} {count}/{slots}
              </button>
            );
          })}
        </div>

        {/* Slot rows — Ulala style: one block per rotation position */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Array.from({ length: DUEL_ROTATION_SIZE }, (_, i) => {
            const slotUnlocked = i < slots;
            const id = slotUnlocked ? current[i] : undefined;
            const def = id ? PVE_SPELLS[id] : null;
            return (
              <div key={i} onClick={() => { if (slotUnlocked) setPicking(i); }} style={{
                display: "flex", alignItems: "center", gap: 12, position: "relative",
                borderRadius: 13, padding: "10px 12px", minHeight: 60,
                cursor: slotUnlocked ? "pointer" : "default",
                background: def ? `linear-gradient(180deg,${def.color}16,${def.color}05)` : "rgba(255,255,255,0.03)",
                border: `1.5px solid ${def ? def.color : slotUnlocked ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.08)"}`,
                boxShadow: def ? `0 0 16px ${def.color}2E` : "none",
                opacity: slotUnlocked ? 1 : 0.5,
              }}>
                {/* Order number */}
                <span style={{
                  flexShrink: 0, width: 22, height: 22, borderRadius: "50%",
                  display: "grid", placeItems: "center", font: `700 12px ${FONT.display}`,
                  background: def ? def.color : "rgba(255,255,255,0.08)", color: def ? "#0c1322" : C.muted,
                }}>{i + 1}</span>

                {/* Icon + mana drop */}
                <div style={{
                  flexShrink: 0, position: "relative", width: 44, height: 44, borderRadius: 12,
                  display: "grid", placeItems: "center", fontSize: 24,
                  background: def ? `${def.color}1c` : "rgba(255,255,255,0.04)",
                  border: `1.5px solid ${def ? def.color + "88" : "rgba(255,255,255,0.16)"}`,
                  boxShadow: def ? `0 0 12px ${def.color}55` : "none",
                }}>
                  {slotUnlocked ? (def ? def.icon : "+") : "🔒"}
                  {def && <ManaDrop cost={def.cost * ROTATION_GAUGE_SCALE} color={def.color} />}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {!slotUnlocked ? (
                    <div style={{ font: `700 11px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted }}>Unlocks at Lv {ROTATION_SLOT_LEVELS[i]}</div>
                  ) : def ? (
                    <>
                      <div style={{ font: `700 14px ${FONT.display}`, letterSpacing: 0.5, color: def.color }}>{def.name}<span style={{ color: C.muted, fontWeight: 700 }}>{dmgLabel(id!)}</span></div>
                      <div style={{ font: `500 10px ${FONT.ui}`, color: "#9fb0c8", lineHeight: 1.3, marginTop: 2 }}>{def.description}</div>
                    </>
                  ) : (
                    <div style={{ font: `700 12px ${FONT.label}`, letterSpacing: 0.5, color: C.muted }}>Tap to choose a spell</div>
                  )}
                </div>

                {slotUnlocked && def && (
                  <button onClick={(e) => { e.stopPropagation(); assign(i, null); }} style={{
                    flexShrink: 0, width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
                    border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)",
                    color: C.muted, font: "700 13px system-ui", lineHeight: 1,
                  }}>×</button>
                )}
              </div>
            );
          })}
        </div>

        {/* Status line — autosave feedback / minimum hint */}
        <div style={{ textAlign: "center", font: `700 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: valid ? C.turf : C.gold }}>
          {valid ? "✓ Saved automatically" : `Pick at least ${MIN_DUEL_ROTATION} spells`}
        </div>

        <button onClick={onClose} style={{ width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 10, padding: "11px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>Close</button>
      </div>

      {/* Spell picker — assign a spell to the tapped slot */}
      {picking !== null && (
        <div onClick={(e) => { e.stopPropagation(); setPicking(null); }} style={{
          position: "fixed", inset: 0, zIndex: 950, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: "100%", maxWidth: 480, height: "92vh",
            background: "linear-gradient(180deg,#18233b,#0b1120)",
            borderTopLeftRadius: 22, borderTopRightRadius: 22,
            borderTop: "1px solid rgba(255,255,255,0.12)", padding: "20px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
            display: "flex", flexDirection: "column", gap: 12, fontFamily: FONT.ui,
          }}>
            <div style={{ flexShrink: 0, font: `700 15px ${FONT.display}`, letterSpacing: 2, textTransform: "uppercase", color: C.text, textAlign: "center" }}>
              Slot {picking + 1} · {tab === "attack" ? "Attack" : "Defense"}
            </div>
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              {SPELL_POOL.map((id) => {
                const def = PVE_SPELLS[id];
                const isUnlocked = unlocked.includes(id);
                const on = current[picking] === id;
                const usedAt = current.indexOf(id);
                return (
                  <button key={id} onClick={() => assign(picking, id)} disabled={!isUnlocked} style={{
                    position: "relative", textAlign: "left", borderRadius: 13, padding: "10px 12px",
                    display: "flex", alignItems: "center", gap: 12,
                    background: on ? `linear-gradient(180deg,${def.color}18,${def.color}05)` : "rgba(255,255,255,0.03)",
                    border: `1.5px solid ${on ? def.color : "rgba(255,255,255,0.1)"}`,
                    boxShadow: on ? `0 0 16px ${def.color}33` : "none",
                    opacity: isUnlocked ? 1 : 0.4,
                    cursor: isUnlocked ? "pointer" : "not-allowed",
                  }}>
                    {/* Emoji cube + mana drop */}
                    <div style={{
                      flexShrink: 0, position: "relative", width: 44, height: 44, borderRadius: 12,
                      display: "grid", placeItems: "center", fontSize: 24,
                      background: `${def.color}1c`, border: `1.5px solid ${def.color}88`,
                      boxShadow: isUnlocked ? `0 0 12px ${def.color}55` : "none",
                    }}>
                      {def.icon}
                      <ManaDrop cost={def.cost * ROTATION_GAUGE_SCALE} color={def.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ font: `700 14px ${FONT.display}`, letterSpacing: 0.5, color: isUnlocked ? C.text : C.muted }}>{def.name}<span style={{ color: C.muted, fontWeight: 700 }}>{dmgLabel(id)}</span></span>
                        {usedAt >= 0 && usedAt !== picking && (
                          <span style={{ font: `700 9px ${FONT.label}`, letterSpacing: 0.5, color: C.muted }}>· slot {usedAt + 1}</span>
                        )}
                      </div>
                      <div style={{ font: `500 10px ${FONT.ui}`, color: "#9fb0c8", lineHeight: 1.3, marginTop: 2 }}>{def.description}</div>
                      {!isUnlocked && (
                        <div style={{ font: `700 9px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted, marginTop: 4 }}>🔒 Level {def.unlockLevel}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setPicking(null)} style={{
              flexShrink: 0, width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)",
              color: C.text, borderRadius: 10, padding: "12px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer",
            }}>‹ Back</button>
          </div>
        </div>
      )}
    </div>
  );
}
